import { useState } from "react";
import { Link } from "react-router-dom";
import { mockFactoryOrders } from "@/mocks/factory";

type Order = typeof mockFactoryOrders[0];

const statusConfig: Record<string, { label: string; color: string; icon: string; bg: string }> = {
  processing: { label: "Processing", color: "text-amber-700", icon: "ri-loader-2-line", bg: "bg-amber-100" },
  shipped: { label: "Shipped", color: "text-blue-700", icon: "ri-truck-line", bg: "bg-blue-100" },
  delivered: { label: "Delivered", color: "text-green-700", icon: "ri-checkbox-circle-line", bg: "bg-green-100" },
  cancelled: { label: "Cancelled", color: "text-red-700", icon: "ri-close-circle-line", bg: "bg-red-100" },
};

const paymentConfig: Record<string, { label: string; color: string }> = {
  paid: { label: "Paid", color: "text-green-700" },
  refunded: { label: "Refunded", color: "text-red-600" },
  pending: { label: "Pending", color: "text-amber-600" },
};

const FactoryOrdersPage = () => {
  const [orders, setOrders] = useState<Order[]>(mockFactoryOrders);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const filtered = orders.filter((o) => {
    const matchStatus = filterStatus === "all" || o.status === filterStatus;
    const matchSearch =
      o.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.buyer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.product.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchSearch;
  });

  const totalRevenue = orders
    .filter((o) => o.paymentStatus === "paid")
    .reduce((sum, o) => sum + o.total, 0);

  const updateStatus = (id: string, newStatus: string) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, status: newStatus as Order["status"] } : o))
    );
    if (selectedOrder?.id === id) {
      setSelectedOrder((prev) => prev ? { ...prev, status: newStatus as Order["status"] } : null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f7f5] flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 bg-[#1a0a0a] flex-col fixed inset-y-0 left-0">
        <div className="p-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="Crown Prime Mall"
              className="h-9 w-9 rounded-full object-cover"
            />
            <div>
              <div className="text-white font-bold text-sm">Crown Prime Mall</div>
              <div className="text-[#D4AF37] text-[10px]">Supplier Portal</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {[
            { icon: "ri-dashboard-line", label: "Dashboard", to: "/factory/dashboard", active: false },
            { icon: "ri-box-3-line", label: "My Products", to: "/factory/products", active: false },
            { icon: "ri-shopping-bag-3-line", label: "Orders", to: "/factory/orders", active: true },
            { icon: "ri-bar-chart-2-line", label: "Analytics", to: "/factory/dashboard", active: false },
            { icon: "ri-message-3-line", label: "Messages", to: "/factory/dashboard", active: false },
            { icon: "ri-settings-3-line", label: "Settings", to: "/factory/dashboard", active: false },
          ].map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                item.active ? "bg-[#C41E3A] text-white" : "text-gray-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={item.icon}></i>
              </div>
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <Link to="/factory/login" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-white/10 hover:text-white transition-colors cursor-pointer">
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-logout-box-line"></i></div>
            Sign Out
          </Link>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Orders</h1>
            <p className="text-xs text-gray-400">{orders.length} total orders &bull; ${totalRevenue.toLocaleString()} revenue</p>
          </div>
          <button className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-download-line"></i> Export CSV
          </button>
        </header>

        <main className="flex-1 p-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: "All Orders", count: orders.length, color: "text-gray-900", bg: "bg-gray-100", icon: "ri-file-list-3-line" },
              { label: "Processing", count: orders.filter(o => o.status === "processing").length, color: "text-amber-700", bg: "bg-amber-100", icon: "ri-loader-2-line" },
              { label: "Shipped", count: orders.filter(o => o.status === "shipped").length, color: "text-blue-700", bg: "bg-blue-100", icon: "ri-truck-line" },
              { label: "Delivered", count: orders.filter(o => o.status === "delivered").length, color: "text-green-700", bg: "bg-green-100", icon: "ri-checkbox-circle-line" },
            ].map((card) => (
              <div key={card.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${card.bg}`}>
                  <i className={`${card.icon} ${card.color} text-lg`}></i>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${card.color}`}>{card.count}</div>
                  <div className="text-xs text-gray-500">{card.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <div className="relative flex-1 max-w-sm">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search order ID, buyer, product..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#C41E3A] transition-colors"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {["all", "processing", "shipped", "delivered", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                    filterStatus === s
                      ? "bg-[#C41E3A] text-white border-[#C41E3A]"
                      : "border-gray-200 text-gray-600 hover:border-[#C41E3A]"
                  }`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Order ID</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Buyer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Product</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Qty</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Total</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((order) => {
                    const s = statusConfig[order.status];
                    const p = paymentConfig[order.paymentStatus];
                    return (
                      <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-4">
                          <div className="text-sm font-bold text-gray-900">{order.id}</div>
                          <div className={`text-xs font-semibold ${p.color}`}>{p.label}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-semibold text-gray-900">{order.buyer}</div>
                          <div className="text-xs text-gray-400">{order.buyerCountry === "USA" ? "🇺🇸" : "🇨🇦"} {order.buyerCountry}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-700 max-w-[180px] truncate">{order.product}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-semibold text-gray-900">{order.qty}</span>
                          <div className="text-xs text-gray-400">units</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-bold text-gray-900">${order.total.toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full w-fit ${s.bg} ${s.color}`}>
                            <i className={s.icon}></i> {s.label}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-xs text-gray-500">{order.date}</span>
                        </td>
                        <td className="px-4 py-4">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="flex items-center gap-1 text-xs text-[#C41E3A] font-semibold hover:underline cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-eye-line"></i> View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-shopping-bag-3-line text-4xl"></i>
                  </div>
                  <p className="text-sm">No orders found</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Order Detail Drawer */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-base font-bold text-gray-900">{selectedOrder.id}</h2>
                <p className="text-xs text-gray-400">{selectedOrder.date}</p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">ORDER STATUS</p>
                <div className="flex gap-2 flex-wrap">
                  {["processing", "shipped", "delivered", "cancelled"].map((s) => {
                    const cfg = statusConfig[s];
                    return (
                      <button
                        key={s}
                        onClick={() => updateStatus(selectedOrder.id, s)}
                        className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-all cursor-pointer whitespace-nowrap ${
                          selectedOrder.status === s
                            ? `${cfg.bg} ${cfg.color} border-current`
                            : "border-gray-200 text-gray-500 hover:border-gray-400"
                        }`}
                      >
                        <i className={cfg.icon}></i> {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Buyer Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 mb-3">BUYER INFORMATION</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-building-2-line text-gray-400 text-sm"></i>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{selectedOrder.buyer}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-gray-400 text-sm"></i>
                    </div>
                    <span className="text-sm text-gray-600">{selectedOrder.shippingAddress}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-flag-line text-gray-400 text-sm"></i>
                    </div>
                    <span className="text-sm text-gray-600">{selectedOrder.buyerCountry === "USA" ? "🇺🇸 United States" : "🇨🇦 Canada"}</span>
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 mb-3">ORDER DETAILS</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Product</span>
                    <span className="text-sm font-semibold text-gray-900 text-right max-w-[200px]">{selectedOrder.product}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Quantity</span>
                    <span className="text-sm font-semibold text-gray-900">{selectedOrder.qty} units</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                    <span className="text-sm font-bold text-gray-900">Total</span>
                    <span className="text-sm font-bold text-[#C41E3A]">${selectedOrder.total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Payment</span>
                    <span className={`text-sm font-semibold ${paymentConfig[selectedOrder.paymentStatus].color}`}>
                      {paymentConfig[selectedOrder.paymentStatus].label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-xl text-sm font-semibold hover:bg-[#1ebe5d] transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-whatsapp-line"></i> Contact Buyer on WhatsApp
                </button>
                <button className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-700 py-3 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-printer-line"></i> Print Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FactoryOrdersPage;
